import React from 'react';
import { FileText, Info } from 'lucide-react';
import { getTimezoneDisplayInfo } from '@/utils/countryTimezones';
import InfoTooltip from '../../common/InfoTooltip';
import { GoogleCalendarIntegrationCard } from './GoogleCalendarIntegrationCard';
import { WebPushNotificationsCard } from './WebPushNotificationsCard';
import { useIsPwaApp } from '../../../pwa/PwaModeContext';

type ProfileData = {
  name: string;
  lastName: string;
  birthDate: string;
  phone: string;
  profileImage: string | null;
};

interface ProfileGeneralTabProps {
  profileData: ProfileData;
  joinDate: string;
  timezone?: string | null;
  onGuideReplay: () => void;
  onMisDocumentosClick: () => void;
  onTimezoneChangeClick?: () => void;
}

function formatDisplayDate(raw: string): string {
  if (!raw) return '';
  // Already formatted (e.g. "5 de diciembre de 2025")
  if (/[a-zA-Z]/.test(raw)) return raw;
  // ISO date "YYYY-MM-DD" → "DD/MM/YYYY"
  const parts = raw.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return raw;
}

interface FieldCardProps {
  label: string;
  value: string;
  labelColor?: string;
}

const FieldCard: React.FC<FieldCardProps> = ({ label, value, labelColor = 'text-gray-400' }) => (
  <div className="bg-gray-50 rounded-xl px-3 pt-2.5 pb-3 sm:px-4 sm:pt-3 sm:pb-4 min-w-0">
    <p className={`text-[10px] sm:text-[11px] font-medium uppercase tracking-wider mb-1 sm:mb-1.5 ${labelColor}`}>{label}</p>
    <p className="text-xs sm:text-sm font-normal text-gray-800 break-words">{value || '—'}</p>
  </div>
);

export const ProfileGeneralTab: React.FC<ProfileGeneralTabProps> = ({
  profileData,
  joinDate,
  timezone,
  onGuideReplay,
  onMisDocumentosClick,
  onTimezoneChangeClick,
}) => {
  // App mobile: sin onboarding guiado y sin la conexión con Google Calendar (ambos solo web).
  const isPwaApp = useIsPwaApp();
  const tzDisplay = getTimezoneDisplayInfo(timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  return (
    <div>
      <div className="flex items-start justify-between gap-2 mb-4 sm:mb-5">
        <h3 className="section-title !mb-0 flex-1">Información general</h3>
        {!isPwaApp && (
        <button
          type="button"
          onClick={onGuideReplay}
          title="Repasar guía de Información General"
          aria-label="replay-profile-onboarding"
          className="flex-shrink-0 inline-flex items-center gap-1 text-[0.75rem] font-normal text-[#8475b3] hover:text-[#6D57BC] bg-transparent border-0 cursor-pointer transition-colors"
        >
          <Info size={14} />
          Repasar guía de Información General
        </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-3">
        <FieldCard label="Nombre" value={profileData.name} />
        <FieldCard label="Apellido" value={profileData.lastName} />
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-3">
        <FieldCard label="Fecha de nacimiento" value={formatDisplayDate(profileData.birthDate)} />
        <FieldCard label="Número de teléfono" value={profileData.phone} />
      </div>

      {/* Gestión de huso horario */}
      <div className="bg-gray-50 rounded-xl px-3 pt-2.5 pb-3 sm:px-4 sm:pt-3 sm:pb-4 min-w-0 flex items-center justify-between gap-2 mb-4 sm:mb-5">
          <div className="min-w-0">
            <p className="text-[10px] sm:text-[11px] font-medium uppercase tracking-wider mb-1 sm:mb-1.5 text-gray-400">
              Huso horario
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              {tzDisplay.flagUrl && (
                <img src={tzDisplay.flagUrl} alt="" className="w-[1.1em] h-[1.1em] shrink-0" />
              )}
              <span className="text-xs sm:text-sm font-normal text-gray-800">
                {tzDisplay.label}
              </span>
              <span className="text-xs sm:text-sm text-gray-400">({tzDisplay.offset.replace('UTC', 'GMT')})</span>
            </div>
          </div>
          <div className="flex-shrink-0 flex items-center gap-1.5">
            <button
              type="button"
              onClick={onTimezoneChangeClick}
              className="self-stretch flex items-center text-[10px] sm:text-xs font-semibold text-primary hover:underline"
            >
              Cambiar
            </button>
            <InfoTooltip
              placement="left"
              iconColor="#6D57BC"
              ariaLabel="Qué es el huso horario"
              text="El huso horario es la franja geográfica que comparte la misma hora oficial (por ejemplo, GMT-3 en Argentina). En tu agenda define a qué hora local se muestran y se agendan todas tus sesiones. Si tu huso no coincide con el lugar donde estás, los horarios pueden no coincidir con tu reloj. Por eso, si viajás o te mudás a otra zona, actualizalo para que tu agenda refleje tu hora real."
            />
          </div>
      </div>

      {!isPwaApp && <GoogleCalendarIntegrationCard />}
      {/* Solo web: en la app mobile las notificaciones son una sección de Perfil (PwaNotificaciones). */}
      {!isPwaApp && <WebPushNotificationsCard />}

      <hr className="my-4 sm:my-6 border-gray-200" />

      <h3 className="section-title mb-3 sm:mb-4">Información profesional</h3>

      <div className="mb-3 sm:mb-4">
        <button
          type="button"
          onClick={onMisDocumentosClick}
          className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <FileText size={14} className="text-gray-500" />
          Mi documentación
        </button>
      </div>

      <div className='flex flex-col gap-2'>
        <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Ingresaste al equipo</p>
        <FieldCard label="Fecha de alta en Psi Mammoliti" value={joinDate} labelColor="text-[#6D57BC]" />
      </div>
    </div>
  );
};

export default ProfileGeneralTab;
